/**
 * External dependencies
 */
import React, { useRef, useMemo, useEffect, useCallback } from "react";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { withLocalize, Translate } from "react-localize-redux";
import { useParams } from "react-router-dom";
import { AnimatePresence } from "framer-motion";

/**
 * Internal dependencies
 */
import { PageProps } from "../types";
import { analytics } from "../utils/Analytics";
import { useDimensions } from "../utils/use-dimensions";
import {
  selectOrderLoading,
  addBarber,
  fetchBarber,
  selectOrderBarber,
} from "../features/order/orderSlice";

import { navigateTo } from "../utils/navigation";
import { setPageTitle } from "../utils/seo";
import { getAnalyticsOptions } from "../features/order/utils";
import { useFavoriteBarberIds } from "../features/user/use-favorite-barber-ids";
import { useFocusInterval } from "../utils/use-focus-interval";
import { useRandomBrandImageUrl } from "../features/images/use-random-brand-image-url";

import ApiConstants from "../constants/api";
import BackButton from "../components/BackButton";

import FavoriteToggle from "../components/FavoriteToggle";

import Shifts from "../features/barbers/Shifts";
import OrderErrorHandler from "../features/order/OrderErrorHandler";
import SlideUp from "../features/animations/SlideUp";
import CriticalCTAButton from "../components/CriticalCTAButton";
import Drawer, { useDrawerState } from "../components/Drawer";
import placeholderAvatar from "../assets/images/avatar-placeholder.png";
import { ReactComponent as InfoIcon } from "../assets/images/icon-info.svg";

/**
 * Constants
 */
const EXIT_ROUTE = "/barbers";

const { BARBER_REFRESH_TIMEOUT } = ApiConstants;

const IMAGE_RESPONSIVE_HEIGHT = {
  sm: 230,
  md: 250,
  lg: 325,
};

/**
 * Interface
 */
interface BarberProps extends PageProps {}

const Barber: React.FC<BarberProps> = ({ translate, activeLanguage }) => {
  /**
   * Utils, refs
   */
  const dispatch = useDispatch();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  /**
   * Fetch barbershop data
   */
  const { id: idString } = useParams<any>();
  const pageId = parseInt(idString);

  const memoizedFetchBarber = useCallback(() => {
    dispatch(fetchBarber(pageId));
  }, [dispatch, pageId]);

  useEffect(() => {
    memoizedFetchBarber();
  }, [memoizedFetchBarber]);

  useFocusInterval(memoizedFetchBarber, BARBER_REFRESH_TIMEOUT);

  const barber = useSelector(selectOrderBarber);
  const barberId = barber?.id;

  // Ensure barber data is (re)fetched when returning from other pages that clear state
  useEffect(() => {
    if (pageId && barberId !== pageId) {
      dispatch(fetchBarber(pageId));
    }
  }, [pageId, barberId, dispatch]);

  const {
    firstname,
    id,
    lastname,
    image,
    description,
    status,
    shifts,
    barbershop,
    active_pob,
  } = barber || {};
  const full_name = firstname && lastname ? `${firstname} ${lastname}` : "";
  const short_name = (() => {
    const first = firstname || (full_name ? full_name.split(" ")[0] : "");
    const last = lastname || (full_name ? full_name.split(" ").slice(-1)[0] : "");
    const lastInitial = last ? `${last.charAt(0).toUpperCase()}.` : "";
    return [first, lastInitial].filter(Boolean).join(" ").trim();
  })();
  const hasBarberData = !!(firstname && id);
  const barber_image = image ? image : placeholderAvatar;

  const isOnShiftNow = useMemo(() => {
    if (!shifts) return false;

    const now = new Date();
    const pad2 = (n: number) => (n < 10 ? `0${n}` : `${n}`);
    const todayKey = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(
      now.getDate()
    )}`;
    const minutesNow = now.getHours() * 60 + now.getMinutes();

    const toMinutes = (hhmm: string | undefined) => {
      if (!hhmm) return -1;
      const [h, m] = hhmm.split(":");
      const hh = parseInt(h || "0", 10);
      const mm = parseInt(m || "0", 10);
      return hh * 60 + mm;
    };

    let todays: any[] = [];
    if (Array.isArray(shifts)) {
      todays = (shifts as any[]).filter((s: any) => s?.date === todayKey);
    } else if (typeof shifts === "object" && shifts !== null) {
      todays = (shifts as any)[todayKey] || [];
    }

    if (!todays || todays.length === 0) return false;

    return todays.some((s: any) => {
      const start = toMinutes(s?.start);
      const end = toMinutes(s?.end);
      return start >= 0 && end >= 0 && minutesNow >= start && minutesNow < end;
    });
  }, [shifts]);

  const isAvailable = status === "active" && !!barbershop && isOnShiftNow;

  /**
   * Barbershop loading
   */
  const loading = useSelector(selectOrderLoading);

  useEffect(() => {
    if (loading === "rejected") {
      navigateTo(EXIT_ROUTE, "slide-left");
    }
  }, [loading]);

  /**
   * We will move the customer to barbershop page with the current barber selected
   */
  const handleAcceptClick = useCallback(
    (barber) => {
      dispatch(addBarber(barber));

      navigateTo(`/barbershop/${active_pob?.id}`, "slide-right");
    },
    [dispatch, active_pob?.id]
  );

  /**
   * SEO & Analytics
   */
  useEffect(() => {
    if (activeLanguage && short_name) {
      setPageTitle([translate("title.base"), short_name]);
    }
  }, [short_name, activeLanguage, translate]);

  useEffect(() => {
    if (id && full_name) {
      const { path, title } = getAnalyticsOptions(id, full_name);
      analytics.pageView(path, title);
    }
  }, [id, full_name]);

  /**
   * Image
   */
  const fallbackImage = useRandomBrandImageUrl(id ?? 0);

  const { windowHeight } = useDimensions();

  const imageHeight = useMemo(
    () => getImageHeight(windowHeight),
    [windowHeight]
  );

  /**
   * Favorites
   */
  const favoriteIds = useFavoriteBarberIds();
  const isFavorite = useMemo(() => favoriteIds.includes(id), [favoriteIds, id]);

  /**
   * Service info drawer
   */
  const {
    isOpen: showDrawer,
    content: drawerContent,
    close: closeDrawer,
  } = useDrawerState();

  /**
   * Classnames
   */
  const componentClass = classNames("s-barber", {
    "is-loading": loading === "pending" && !hasBarberData,
  });

  const wrapperClass = classNames("s-barber__wrapper", {
    "h-scrollable": hasBarberData,
  });

  return (
    <div className={componentClass}>
      <div className={wrapperClass} ref={wrapperRef}>
        <div
          className="s-barber__header-image"
          style={{
            backgroundImage: `url(${fallbackImage})`,
            height: imageHeight,
          }}
        />

        <div className="s-barber__content" ref={contentRef}>
          <div className="s-barber__details">
            <div className="s-barber__details__image-wrapper">
              <img
                src={barber_image}
                alt=""
                className="s-barber__details__image"
                onError={(e) => {
                  const target = e.currentTarget as HTMLImageElement;
                  target.onerror = null;
                  target.src = placeholderAvatar;
                }}
              />
            </div>
            <div className="s-barber__details__info">
              <h1 className="s-barber__details__title">{short_name}</h1>
              {isAvailable ? (
                <p className="s-barber__details__subtitle c-barber-card__subtitle">
                  <span className="c-barber-card__ellipse is-available"></span>
                  {/*  <Translate id="barber.now" />
                  {" - "} */}
                  {`${active_pob?.name}, ${active_pob?.city}`}
                </p>
              ) : (
                <p className="s-barber__details__subtitle c-barber-card__subtitle">
                  <span className="c-barber-card__ellipse"></span>
                  <Translate id="barber.notIn" />
                </p>
              )}
            </div>
            {description && (
              <p className="s-barber__description">{description}</p>
            )}

            <div className="s-barber__favorite">
              <FavoriteToggle
                barbershop={barber}
                isBarber={true}
                isFavorite={isFavorite}
              />
            </div>
          </div>

          <div className="s-barber__calendar">
            {shifts?.length !== 0 && (
              <>
                <h2 className="s-barber__calendar-title">
                  {translate("barber.workSchedule")}
                </h2>
                <p className="s-barber__calendar-description">
                  <InfoIcon width={15} height={15} />
                  {translate("barber.scheduleNotice")}
                </p>
              </>
            )}
            {/* This might come later <Calendar
              activeLanguage={activeLanguage?.code}
              label={translate("barber.workSchedule")}
              shifts={shifts || []}
            /> */}
            <Shifts
              shifts={shifts || []}
              activeLanguage={activeLanguage?.code}
            />
          </div>
        </div>
      </div>

      <div className="s-barbershop__back">
        <BackButton fallbackRoute={EXIT_ROUTE} />
      </div>

      {isAvailable && (
        <AnimatePresence>
          <SlideUp
            className="s-barbershop__accept-order"
            duration={0.25}
            bounce={0}
          >
            {/* onClick={} */}
            <CriticalCTAButton
              onClick={() => handleAcceptClick(barber)}
              text={translate("barber.joinQueue")}
            />
          </SlideUp>
        </AnimatePresence>
      )}

      <AnimatePresence>
        {showDrawer && (
          <Drawer onClose={() => closeDrawer()}>{drawerContent}</Drawer>
        )}
      </AnimatePresence>

      <OrderErrorHandler id={id ?? 0} name={full_name} translate={translate} />
    </div>
  );
};

export default withLocalize(Barber);

/**
 * Get image height based on window height.
 */
const getImageHeight = (windowHeight: number): number => {
  const { sm, md, lg } = IMAGE_RESPONSIVE_HEIGHT;

  if (windowHeight < 768) {
    return sm;
  } else if (windowHeight < 900) {
    return md;
  }

  return lg;
};
